package com.evoapps.evoshift.ui.theme

import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.AnimatedContentScope
import androidx.compose.animation.AnimatedContentTransitionScope
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.AnimatedVisibilityScope
import androidx.compose.animation.ContentTransform
import androidx.compose.animation.SizeTransform
import androidx.compose.animation.animateContentSize
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.Easing
import androidx.compose.animation.core.FiniteAnimationSpec
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.VisibilityThreshold
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.snap
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.tween
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.interaction.InteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.lazy.LazyItemScope
import androidx.compose.foundation.lazy.grid.LazyGridItemScope
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.text
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize

/*
 * SH-158: motion inside the screens (navigation is Motion.kt's). Every action answers at once and
 * a change moves into place instead of popping; with reduced motion ([LocalReducedMotion]) the
 * same changes happen without moving (snap).
 */

/** A tween of [durationMs], or a snap when [reduced]. */
fun <T> motionTween(durationMs: Int, reduced: Boolean, easing: Easing = Motion.Emphasized): FiniteAnimationSpec<T> =
    if (reduced) snap() else tween(durationMs, easing = easing)

/** A tween of [durationMs] ([Motion.SHORT_MS] by default) that snaps under reduced motion. */
@Composable
fun <T> motionTween(durationMs: Int = Motion.SHORT_MS, easing: Easing = Motion.Emphasized): FiniteAnimationSpec<T> =
    motionTween(durationMs, LocalReducedMotion.current, easing)

/** A soft spring (things settling into place), or a snap when [reduced]. */
fun <T> motionSpring(reduced: Boolean, stiffness: Float = Spring.StiffnessMediumLow): FiniteAnimationSpec<T> =
    if (reduced) snap() else spring(dampingRatio = Spring.DampingRatioNoBouncy, stiffness = stiffness)

/** A soft spring that snaps under reduced motion. */
@Composable
fun <T> motionSpring(stiffness: Float = Spring.StiffnessMediumLow): FiniteAnimationSpec<T> =
    motionSpring(LocalReducedMotion.current, stiffness)

/**
 * A list row that fades in, glides to its new place and fades out (approved, removed, sorted)
 * instead of jumping; under reduced motion it just appears and goes. The row needs a stable key.
 */
@Composable
fun Modifier.animateItemIfMotion(scope: LazyItemScope): Modifier = with(scope) {
    if (LocalReducedMotion.current) this@animateItemIfMotion.animateItem(null, null, null)
    else this@animateItemIfMotion.animateItem(
        fadeInSpec = tween(Motion.SHORT_MS),
        placementSpec = spring(stiffness = Spring.StiffnessMediumLow, visibilityThreshold = IntOffset.VisibilityThreshold),
        fadeOutSpec = tween(Motion.SHORT_MS)
    )
}

/** [animateItemIfMotion] for a lazy grid's cell. */
@Composable
fun Modifier.animateItemIfMotion(scope: LazyGridItemScope): Modifier = with(scope) {
    if (LocalReducedMotion.current) this@animateItemIfMotion.animateItem(null, null, null)
    else this@animateItemIfMotion.animateItem(
        fadeInSpec = tween(Motion.SHORT_MS),
        placementSpec = spring(stiffness = Spring.StiffnessMediumLow, visibilityThreshold = IntOffset.VisibilityThreshold),
        fadeOutSpec = tween(Motion.SHORT_MS)
    )
}

/** A box whose size follows its content smoothly (a line added or removed); instant under reduced motion. */
@Composable
fun Modifier.motionContentSize(): Modifier =
    if (LocalReducedMotion.current) this
    else animateContentSize(spring(stiffness = Spring.StiffnessMediumLow, visibilityThreshold = IntSize.VisibilityThreshold))

/**
 * A part of a screen that comes and goes (a note, a button, an error): it unfolds and fades in,
 * folds and fades out, so what is under it slides instead of jumping. Snaps under reduced motion.
 */
@Composable
fun MotionVisibility(visible: Boolean, modifier: Modifier = Modifier, content: @Composable AnimatedVisibilityScope.() -> Unit) {
    val reduced = LocalReducedMotion.current
    AnimatedVisibility(
        visible = visible,
        modifier = modifier,
        enter = fadeIn(motionTween(Motion.SHORT_MS, reduced)) + expandVertically(motionTween(Motion.MEDIUM_MS, reduced), expandFrom = Alignment.Top),
        exit = fadeOut(motionTween(Motion.SHORT_MS, reduced)) + shrinkVertically(motionTween(Motion.MEDIUM_MS, reduced), shrinkTowards = Alignment.Top),
        content = content
    )
}

/**
 * The new contents rise into place while the old ones rise away (a label, an icon, a figure): a
 * short odometer-like swap. Under reduced motion the contents just change.
 */
fun <S> AnimatedContentTransitionScope<S>.swapUp(reduced: Boolean): ContentTransform =
    if (reduced) {
        fadeIn(snap()) togetherWith fadeOut(snap())
    } else {
        (slideInVertically(motionTween(Motion.SHORT_MS, false)) { it / 3 } + fadeIn(motionTween(Motion.SHORT_MS, false))) togetherWith
            (slideOutVertically(motionTween(Motion.SHORT_MS, false)) { -it / 3 } + fadeOut(motionTween(Motion.SHORT_MS, false))) using
            SizeTransform(clip = false)
    }

/**
 * One part of a screen replaced by another (a button by its form, one day's panel by the next):
 * the old one fades out while the new one fades in, and the box between them resizes smoothly.
 * Under reduced motion it just changes.
 */
fun fadeThrough(reduced: Boolean): ContentTransform = ContentTransform(
    targetContentEnter = fadeIn(motionTween(Motion.SHORT_MS, reduced)),
    initialContentExit = fadeOut(motionTween(Motion.SHORT_MS, reduced)),
    sizeTransform = SizeTransform(clip = false) { _, _ -> motionSpring(reduced) }
)

/**
 * [AnimatedContent] with [fadeThrough]: [content] for [targetState], the previous one fading out.
 * Keep state that must survive the swap outside [content] (each state's contents are their own).
 */
@Composable
fun <S> MotionContent(
    targetState: S,
    modifier: Modifier = Modifier,
    label: String = "content",
    content: @Composable AnimatedContentScope.(S) -> Unit
) {
    val reduced = LocalReducedMotion.current
    AnimatedContent(
        targetState = targetState,
        modifier = modifier,
        transitionSpec = { fadeThrough(reduced) },
        contentAlignment = Alignment.TopStart,
        label = label,
        content = content
    )
}

/**
 * An amount that changes in place ("1 234,00 zł" → "1 284,00 zł"): the new text rises in, the old
 * one rises out, like an odometer (a swap, not counting through the numbers). For accessibility
 * and tests the box reads as exactly one text, the current one, even while the old one leaves.
 */
@Composable
fun AnimatedAmountText(
    text: String,
    modifier: Modifier = Modifier,
    style: TextStyle = LocalTextStyle.current,
    color: Color = Color.Unspecified,
    fontWeight: FontWeight? = null,
    maxLines: Int = Int.MAX_VALUE,
    softWrap: Boolean = true,
    fontStyle: FontStyle? = null,
    /** End: the figure keeps to the right edge of [modifier]'s box (a column of amounts). */
    textAlign: TextAlign? = null,
    overflow: TextOverflow = TextOverflow.Clip,
    /** On the text itself, not the box (an underline as wide as the figure). */
    textModifier: Modifier = Modifier
) {
    val reduced = LocalReducedMotion.current
    AnimatedContent(
        targetState = text,
        modifier = modifier.clearAndSetSemantics { this.text = AnnotatedString(text) },
        transitionSpec = { swapUp(reduced) },
        contentAlignment = when (textAlign) {
            TextAlign.End, TextAlign.Right -> Alignment.TopEnd
            TextAlign.Center -> Alignment.TopCenter
            else -> Alignment.TopStart
        },
        label = "amount"
    ) { shown ->
        Text(
            shown, textModifier, style = style, color = color, fontWeight = fontWeight, fontStyle = fontStyle,
            textAlign = textAlign, maxLines = maxLines, softWrap = softWrap, overflow = overflow
        )
    }
}

/** A fraction (a progress bar, a budget used) that moves to [target] instead of jumping. */
@Composable
fun animatedFraction(target: Float): Float {
    val value by animateFloatAsState(target, motionTween(Motion.MEDIUM_MS), label = "fraction")
    return value
}

/** How strongly a row still being saved (a placeholder id) shows. */
const val PENDING_ALPHA = 0.55f

/**
 * A row saved optimistically, its id still a placeholder: it fades in to [PENDING_ALPHA] while the
 * save is on its way. The saved row (a new id, so new contents) shows at full strength at once,
 * so the swap to the real id does not replay the fade. No fade under reduced motion.
 */
@Composable
fun Modifier.pendingFade(pending: Boolean): Modifier {
    if (!pending) return this
    val reduced = LocalReducedMotion.current
    val alpha = remember { Animatable(if (reduced) PENDING_ALPHA else 0f) }
    LaunchedEffect(Unit) { alpha.animateTo(PENDING_ALPHA, motionTween(Motion.SHORT_MS, reduced)) }
    return graphicsLayer { this.alpha = alpha.value }
}

/**
 * A big primary target (the clock button) sinks a little while pressed and springs back: press
 * only, never on hover (SH-130 removed hover lifts), and not at all under reduced motion.
 */
@Composable
fun Modifier.pressScale(interactionSource: InteractionSource): Modifier {
    if (LocalReducedMotion.current) return this
    val pressed by interactionSource.collectIsPressedAsState()
    val scale by animateFloatAsState(
        if (pressed) Motion.PRESS_SCALE else 1f,
        spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessMedium),
        label = "pressScale"
    )
    return graphicsLayer { scaleX = scale; scaleY = scale }
}

/**
 * [value], or the last non-null one while it is null: what a part that folds away (a note, an
 * error) keeps showing until it is gone.
 */
@Composable
fun <T : Any> rememberLastNonNull(value: T?): T? {
    val last = remember { mutableStateOf(value) }
    if (value != null) SideEffect { last.value = value }
    return value ?: last.value
}
